import { existsSync, readFileSync, writeFileSync, unlinkSync, rmSync } from 'fs';
import { dirname } from 'path';

export interface FileOperation {
  type: 'create' | 'update' | 'delete';
  path: string;
  content?: string;
  originalContent?: string;
}

export class TransactionManager {
  private operations: FileOperation[] = [];
  private completed: FileOperation[] = [];

  addCreateOperation(path: string, content: string): void {
    this.operations.push({
      type: 'create',
      path,
      content,
    });
  }

  addUpdateOperation(path: string, content: string): void {
    // Store original content for rollback
    const originalContent = existsSync(path) 
      ? readFileSync(path, 'utf-8')
      : undefined;

    this.operations.push({
      type: 'update',
      path,
      content,
      originalContent,
    });
  }

  addDeleteOperation(path: string): void {
    // Store original content for rollback
    const originalContent = existsSync(path)
      ? readFileSync(path, 'utf-8')
      : undefined;

    this.operations.push({
      type: 'delete',
      path,
      originalContent,
    });
  }

  async execute<T>(callback: () => Promise<T>): Promise<T> {
    try {
      // Execute all file operations
      for (const operation of this.operations) {
        this.executeOperation(operation);
        this.completed.push(operation);
      }

      // Execute the main callback
      const result = await callback();

      // Clear operations on success
      this.operations = [];
      this.completed = [];

      return result;
    } catch (error) {
      // Rollback on failure
      this.rollback();
      throw error;
    }
  }

  private executeOperation(operation: FileOperation): void {
    switch (operation.type) {
      case 'create':
      case 'update':
        if (operation.content !== undefined) {
          // Ensure directory exists
          const dir = dirname(operation.path);
          if (!existsSync(dir)) {
            require('fs').mkdirSync(dir, { recursive: true });
          }
          writeFileSync(operation.path, operation.content);
        }
        break;

      case 'delete':
        if (existsSync(operation.path)) {
          unlinkSync(operation.path);
        }
        break;
    }
  }

  private rollback(): void {
    // Rollback in reverse order
    for (const operation of this.completed.reverse()) {
      try {
        this.rollbackOperation(operation);
      } catch (error) {
        // Log but continue rollback
        console.error(`Failed to rollback ${operation.path}:`, error);
      }
    }

    // Clear operations
    this.operations = [];
    this.completed = [];
  }

  private rollbackOperation(operation: FileOperation): void {
    switch (operation.type) {
      case 'create':
        // Delete created file
        if (existsSync(operation.path)) {
          unlinkSync(operation.path);
          // Try to remove empty directories
          this.cleanupEmptyDirs(dirname(operation.path));
        }
        break;

      case 'update':
        // Restore original content
        if (operation.originalContent !== undefined) {
          writeFileSync(operation.path, operation.originalContent);
        } else if (existsSync(operation.path)) {
          // If there was no original content, delete the file
          unlinkSync(operation.path);
        }
        break;

      case 'delete':
        // Restore deleted file
        if (operation.originalContent !== undefined) {
          writeFileSync(operation.path, operation.originalContent);
        }
        break;
    }
  }

  private cleanupEmptyDirs(dirPath: string): void {
    try {
      const files = require('fs').readdirSync(dirPath);
      if (files.length === 0) {
        rmSync(dirPath, { recursive: true, force: true });
        // Try to clean up parent directory
        const parent = dirname(dirPath);
        if (parent !== dirPath && parent !== '.') {
          this.cleanupEmptyDirs(parent);
        }
      }
    } catch (error) {
      // Ignore errors during cleanup
    }
  }

  // Get a summary of pending operations
  getSummary(): { creates: number; updates: number; deletes: number } {
    const summary = {
      creates: 0,
      updates: 0,
      deletes: 0,
    };

    for (const op of this.operations) {
      switch (op.type) {
        case 'create':
          summary.creates++;
          break;
        case 'update':
          summary.updates++;
          break;
        case 'delete':
          summary.deletes++;
          break;
      }
    }

    return summary;
  }

  // Get list of files that will be affected
  getAffectedFiles(): string[] {
    return this.operations.map(op => op.path);
  }
}